/*
 * Copyright (c) 2005-2024, Paycheck Planner
 * All rights reserved
 * 
 */

const gen = require('./parser/generator.js');

import { formatDisplayDate, monthDiff, cleanDate, getZeroBasedMonthLabel } from './date-helpers.js';

import * as utils from "./utils.js";

import frequency from './frequency.js';
import { Categories } from "./categories.js";
import { model_runtime } from './runtime.js';
import { MM } from "./model_manager.js";
import { SimulationModel } from './simulation-model.js';



const SUBTYPE_NONE = 0;

const SUBTYPE_NETPAY = 1;

const SUBTYPE_FIXED = 2;
const SUBTYPE_PCTSRC = 3;
const SUBTYPE_PCTDST = 4;


const subTypes = [
  { code: "Netpay", type: MM.ITEMTYPE_INCOME, subType: SUBTYPE_NETPAY, desc: "Net Pay" },

  { code: "Fixed", type: MM.ITEMTYPE_TRANSFER, subType: SUBTYPE_FIXED, desc: "Fixed" },
  { code: "PctOfSource", type: MM.ITEMTYPE_TRANSFER, subType: SUBTYPE_PCTSRC, desc: "Percent Of Source" },
  { code: "PctOfDest", type: MM.ITEMTYPE_TRANSFER, subType: SUBTYPE_PCTDST, desc: "Percent Of Destination" },
];


const INIT_TRANSTYPE_ACCT = 1;
const INIT_TRANSTYPE_ITEM = 2;
const INIT_TRANSTYPE_LOAN = 3;
const INIT_TRANSTYPE_NETWORTH = 6;


//---------------------------------------------------------------------------------------
//---------------------------------------------------------------------------------------

class Simulator {
  constructor() {
    this.generator = new gen.Generator();

    console.log("Simulator constructor");
  }


  //---------------------------------------------------------------------------------------
  // Public helper functions, might move from here
  //---------------------------------------------------------------------------------------


  /**
   * Calling this function will fill list with a subset of the available category types
   * that are used in a given model. list starts out empty.
   * @param {*} list 
   * @param {*} name 
   * @returns 
   */
  getCategoryIndex(list, name) {
    // If the list is empty, its the first call, so put in the catchall category
    let len = list.length;

    if (len === 0) {
      let u = Categories.findCategory("Unknown");
      list.push({ cat: u, idx: list.length });
      u = Categories.findCategory("EarnedInterest");
      list.push({ cat: u, idx: list.length });
      u = Categories.findCategory("ChargedInterest");
      list.push({ cat: u, idx: list.length });
      u = Categories.findCategory("Insurance");
      list.push({ cat: u, idx: list.length });
      u = Categories.findCategory("Inctax");
      list.push({ cat: u, idx: list.length });

      len = list.length;
    }

    let cat = Categories.findCategory(name);
    if (cat === null) {
      return 0; // unknown
    }

    // See if we have it already
    let pos = -1;
    for (let i = 0; i < len; i++) {
      if (utils.stringCompare(list[i].cat.longCode, name) === true) {
        return i;
      }
    }

    list.push({ cat: cat, idx: len });

    return len;
  }


  //---------------------------------------------------------------------------------------
  //---------------------------------------------------------------------------------------
  // Transaction Generation
  //---------------------------------------------------------------------------------------
  //---------------------------------------------------------------------------------------

  sortTrans(a, b) {
    /*  
      if ( a.year < b.year ) {
        return -1;
      } else if ( a.year > b.year ) {
        return 1;
      } else {
        if ( a.month < b.month ) {
          return -1;
        } else if ( a.month > b.month ) {
          return 1;
        } else {
          if ( a.day < b.day ) {
            return -1;
          } else if ( a.day > b.day ) {
            return 1;
          }
        }
      }
    */
    var diff = a.dateNum - b.dateNum;
    if (diff === 0) {
      diff = a.initTranType - b.initTranType;
      if (diff == 0) {
        return a.itemType - b.itemType;  // Want incomes first
      }
    }

    return diff;
  }


  /**
  * @param {SimulationEnvironment} env
  * @param {Model} model
  * @param {Symbols} symbols
  * @returns {SimulationResult}
  */
  runSimulation(env, model, applyScenarios) {
    const begin_time = Date.now();


    let sim_model = new SimulationModel(model.name);

    this.setupCategoryTypes(sim_model);
    this.setupSimulationParameters(model, sim_model);
    this.setupSimulationMilestones(model, sim_model);

    if (applyScenarios) {
      this.overrideSimulationParameters(env, sim_model);
    }

    this.setupSimulationAccounts(model, sim_model);
    this.setupSimulationItems(model, sim_model);
    this.setupSimulationLoans(model, sim_model);
    this.setupSimulationAssets(model, sim_model);

    // 19-Jun-2024 JC Step 1, move the date evaluation here past the item setups above.
    //             We may be able to move more here as we go.
    this.parseAndBind(env, model, sim_model);

    this.setupSimulationScenarios(model, sim_model);

    this.setupSimulationEnvironment(env, sim_model, applyScenarios);


    let net_worth = this.calculateNetWorthAtDate(sim_model, new Date());

    // When we setup the accounts, we determined the oldest last update of an account 
    // and we will use this date to compute the first day of the month it occured and this 
    // will be the start of the simulation. For accounts that were updated after that date, 
    // we will just backfill transactions for the calendar so that the balance on the actual
    // update date for that account will be correct.
    const model_start = new Date(sim_model.startDate.getFullYear(), sim_model.startDate.getMonth(), 1);
    const model_end = sim_model.getMilestoneDate("ModelEnd");

    let timeline_res = this.generateTimeline(sim_model, model_start, model_end);

    const setup_time = Date.now() - begin_time;


    //####################################################################################
    let init_tran_ret = this.generateInitialTransactions(env, timeline_res, sim_model);
    let initial_trans = init_tran_ret.initialTrans;


    const gen_time = Date.now() - begin_time - setup_time;

    initial_trans.sort(this.sortTrans);

    const sort_time = Date.now() - begin_time - setup_time - gen_time;

    let final_trans_res = this.processTransactions(sim_model, initial_trans);

    this.generateTransactionSummary(timeline_res, sim_model.accts, sim_model.summary);

    const calc_time = Date.now() - begin_time - setup_time - gen_time - sort_time;



    const res = {
      setupTime: setup_time,
      genTime: gen_time,
      sortTime: sort_time,
      calcTime: calc_time,

      initialTrans: initial_trans,

      timeSpan: timeline_res,

      simModel: sim_model,

      yearlyNetWorth: final_trans_res.netWorth,
      currentNetWorth: net_worth,
    }

    return res;
  }


  addReportingPeriod(simModel, from, to, desc, inPast = false) {
    let period_obj = simModel.createReportingPeriod(from, to, desc, inPast);

    period_obj.displayLabel = desc;
  }



  //---------------------------------------------------------------------------------------
  //---------------------------------------------------------------------------------------
  //    private functions
  //---------------------------------------------------------------------------------------
  //---------------------------------------------------------------------------------------



  //---------------------------------------------------------------------------------------
  // Transaction Generation
  //---------------------------------------------------------------------------------------

  setupCategoryTypes(simModel) {
    let cat_groups = Categories.getCatGroupTypes();

    for (let g = 0; g < cat_groups.length; g++) {
      const cc = cat_groups[g];

      simModel.createCategoryType(g, cc);
    }
  }


  setupSimulationParameters(model, simModel) {
    let len = model.params.length;
    for (let i = 0; i < len; i++) {
      const param = model.params[i];

      let type = 0;
      if (param.paramType === "Date") {
        type = 2; //VM.PARAMTYPE_DATE;  TODO
      }
      else if (param.paramType === "Integer") {
        type = 1; //VM.PARAMTYPE_INT;  TODO
      }

      let p = simModel.createParameter(param.id, param.name, type);

      if (type === 2) {
        p.value = cleanDate(param.val, 0);
      }
      else if (type === 1) {
        p.value = parseInt(param.val);
      }

      p.adjustedValue = p.value;
    }
  }


  setupSimulationMilestones(model, simModel) {
    let len = model.milestones.length;
    for (let i = 0; i < len; i++) {
      const milestone = model.milestones[i];

      let p = simModel.createMilestone(milestone.id, milestone.name, milestone.desc, milestone.expr);
    }
  }


  /**
   * 
   * @param {Model} model
   * @param {SimulationModel} simModel
   */
  setupSimulationAccounts(model, simModel) {
    let min_start_date = new Date();

    const num_accts = model.accounts.length;
    for (let i = 0; i < num_accts; i++) {
      const acct = model.accounts[i];

      const acct_type = MM.getAcctTypeAsConst(acct.type);
      const group_type = MM.getAcctGroupFromAcctType(acct_type);
      const acct_group = group_type.group;

      const pct = parseFloat(acct.interest);
      const rate = pct / 1200.0;


      let service_acct_id = -1;
      if (acct_type === MM.ACCTTYPE_LINEOFCREDIT) {
        service_acct_id = acct.serviceAccountID;

        if (service_acct_id === -1) {
          throw "Invalid Service Acct ID";
        }
      }


      let bal = parseFloat(acct.balance);
      let limit = parseFloat(acct.limit);
      let threshold = parseFloat(acct.threshold);
      let taxrate = parseFloat(acct.taxrate); // %
      // 27-Jul-2024 JC At this time user can enter positive or negative for credit balances
      // if entered postive, convert to negative internally
      if (!group_type.isAsset) {
        if (bal > 0) {
          bal = -1.0 * bal;
        }
        if (limit > 0) {
          limit = -1.0 * limit;
        }
        if (threshold > 0) {
          threshold = -1.0 * threshold;
        }
      }

      const last_update = new Date(acct.lastUpdated);
      const date_num = this.getDateNumFromDate(last_update);

      if (last_update < min_start_date) {
        min_start_date = last_update;
      }

      let last_update_diff = new Date().getTime() - last_update.getTime();
      let last_update_days = last_update_diff / (1000 * 3600 * 24);


      let sim_acct = simModel.createAccount(acct.id, acct.desc);

      sim_acct.isPrimary = acct.isPrimary;
      sim_acct.disableInGraph = acct.disableInGraph;

      sim_acct.lastUpdate = last_update;
      sim_acct.lastUpdateNum = date_num;
      sim_acct.lastUpdateDays = last_update_days;

      sim_acct.acctType = acct_type;
      sim_acct.acctGroup = acct_group;
      sim_acct.isAsset = group_type.isAsset;


      sim_acct.serviceAcctId = service_acct_id;
      sim_acct.serviceDay = acct.serviceDay;

      sim_acct.isTaxable = acct.isTaxable;

      sim_acct.rate = rate;
      sim_acct.limit = limit;
      sim_acct.threshold = threshold;
      sim_acct.taxrate = taxrate;

      sim_acct.hasInsurance = false;
      if (sim_acct.acctType === MM.ACCTTYPE_LINEOFCREDIT) { // TODO - temp, will be in model
        sim_acct.hasInsurance = true;
        sim_acct.insurance = { rate: 0.71, jointOwnerMod: 1.7, premTax: .15 };
      }

      sim_acct.startingBal = bal;
      sim_acct.runningBal = bal;

      sim_acct.delinquentAmount = 0;

    } // foreach account


    // Fixup the service accounts now that they all exist
    for (let i = 0; i < simModel.accts.length; i++) {
      const acct = simModel.accts[i];

      if (acct.acctType != MM.ACCTTYPE_LINEOFCREDIT) {
        continue;
      }

      acct.serviceSimAcct = simModel.findAccountById(acct.serviceAcctId);

      if (acct.serviceSimAcct === null) {
        throw "Invalid Service Sim Acct ID";
      }
    }


    simModel.startDate = min_start_date;
  }


  setupSimulationItems(model, simModel) {
    // First we generate transactions for each active item based on their frequency
    const items = model.items; //.filter( (item) => { return item.active === true; } );


    const num_items = items.length;
    for (let i = 0; i < num_items; i++) {
      const item = items[i];


      const freq_num = frequency.getFreqAsConst(item.freq);

      const item_type = MM.getItemTypeAsConst(item.itemType);
      const sub_type = this.getSubTypeAsConst(item.subType);

      const from_sim_acct = simModel.findAccountById(item.srcActID)
      const to_sim_acct = simModel.findAccountById(item.dstActID)

      let from_idx = 0;
      let to_idx = 0;

      if (item_type === MM.ITEMTYPE_EXPENSE) {
        from_idx = this.getCategoryIndex(simModel.summary, item.category);
      } else if (item_type === MM.ITEMTYPE_INCOME) {
        to_idx = this.getCategoryIndex(simModel.summary, "Income");
      } else {
        let to_cat = "FB";
        if (from_sim_acct.acctType === MM.ACCTTYPE_BANK) {
          to_cat = "FB";
        } else if (from_sim_acct.acctType === MM.ACCTTYPE_CREDITCARD || from_sim_acct.acctType === MM.ACCTTYPE_LINEOFCREDIT) {
          to_cat = "FC";
        } else if (from_sim_acct.acctType === MM.ACCTTYPE_INVESTMENT) {
          to_cat = "FI";
        }

        let from_cat = "TB";
        if (to_sim_acct.acctType === MM.ACCTTYPE_BANK) {
          from_cat = "TB";
        } else if (to_sim_acct.acctType === MM.ACCTTYPE_CREDITCARD || to_sim_acct.acctType === MM.ACCTTYPE_LINEOFCREDIT) {
          from_cat = "TC";
        } else if (to_sim_acct.acctType === MM.ACCTTYPE_INVESTMENT) {
          from_cat = "TI";
        }

        from_idx = this.getCategoryIndex(simModel.summary, from_cat);
        to_idx = this.getCategoryIndex(simModel.summary, to_cat);
      }


      var item_ref = simModel.createItem(item.id, item.desc);
      item_ref.baseName = item.name; // TODO - might be temp, see sim model

      item_ref.active = item.active;
      item_ref.adjustedActive = item.active;

      item_ref.itemType = item_type;
      item_ref.subType = sub_type;

      item_ref.fromCatIdx = from_idx;
      item_ref.toCatIdx = to_idx;

      item_ref.freqNum = freq_num;
      item_ref.amount = item.amount;

      item_ref.startSource = item.startExpr,
      item_ref.endSource = item.stopExpr,

      item_ref.srcActRef = from_sim_acct,

      item_ref.proposedAmount = parseFloat(item.amount); // TODO  - better parsing - handle percentages and possibly formulas??
      // If we have starting condition modifiers, apply them here
      item_ref.adjustedAmount = item_ref.proposedAmount;


      item_ref.fromSimAcct = from_sim_acct;
      item_ref.toSimAcct = to_sim_acct;


      /*
            // TODO - this will become an array
            item.exceptExpr = "10*10";
            if ( item.exceptExpr.length > 0 ) {
              const res = this.generator.registerRule(item.exceptExpr);
              if (!res.error) {
                item_ref.ruleIDs.push(res.id);
              }
            }
      */
    } // foreach item
  }


  setupSimulationLoans(model, simModel) {
    const num_loans = model.loans.length;
    for (var i = 0; i < num_loans; i++) {
      const loan = model.loans[i];

      // TODO - NOTE this is similar code to setting up Item
      const from_sim_acct = simModel.findAccountById(loan.srcActID)

      const original_amount = parseFloat(loan.originalBalance);

      const payment = parseFloat(loan.mortgagePayment);
      const insurance = parseFloat(loan.insurance);
      const tax = parseFloat(loan.propertyTax);
      const total_payment = payment + insurance + tax;


      const pct = parseFloat(loan.interestRate);
      const rate = pct / 1200.0;

      var sim_asset = simModel.createLoan(loan.id, loan.desc);
      sim_asset.name = loan.name;

      sim_asset.fromSimAcct = from_sim_acct;

      sim_asset.period = loan.term * 12;
      sim_asset.rate = rate;

      sim_asset.originalAmount = original_amount;

      sim_asset.mortgagePayment = payment;
      sim_asset.insurance = insurance;
      sim_asset.tax = tax;
      sim_asset.totalPayment = total_payment;

      sim_asset.currentValue = -50000; //cur_val
    }
  }


  setupSimulationAssets(model, simModel) {
    const num_assets = model.assets.length;
    for (var i = 0; i < num_assets; i++) {
      const asset = model.assets[i];

      const cur_val = parseFloat(asset.currentValue);

      var sim_asset = {
        assetID: asset.id,
        assetDesc: asset.desc,

        currentValue: cur_val
      }

      simModel.assets.push(sim_asset);
    }
  }


  setupSimulationScenarios(model, simModel) {
    const num_scenarios = model.scenarios.length;
    for (let i = 0; i < num_scenarios; i++) {
      const scenario = model.scenarios[i];

      if (scenario.active !== true) {
        continue;
      }

      const num_elements = scenario.elements.length;
      for (let e = 0; e < num_elements; e++) {
        const element = scenario.elements[e];

        if (element.active !== true) {
          continue;
        }

        var res = null; //model_runtime.parseScenarioElement(element.expr);
        if (res.errors === true) {
          // notifyCantHappen()
          continue;
        }

        var sim_element = simModel.createScenario();

        const item = simModel.findItemByName(res.stmt.item);
        if (item === null) {
          // notifyCantHappen()
          continue;
        }

        sim_element.adjustmentAmount = parseFloat(res.stmt.amount);

        let eval_res = null; //model_runtime.evaluateExpression(simModel.symbols, res.stmt.startDate); // TODO better error handling here
        sim_element.startNum = this.getDateNumFromDate(simModel.symbols, eval_res.dateVal);

        if (res.stmt.endDate.length > 0) {
          eval_res = null; //model_runtime.evaluateExpression(res.stmt.endDate); // TODO better error handling here
          sim_element.endNum = this.getDateNumFromDate(eval_res.dateVal);
        }

        item.modifiers.push(sim_element);
      }

    }

  }


  overrideSimulationParameters(env, simModel) {
    for (let i = 0; i < env.paramControls.length; i++) {
      const ctrl = env.paramControls[i];

      const param = simModel.findParameterById(ctrl.id);
      param.adjustedValue = ctrl.value;
    }
  }


  setupSimulationEnvironment(env, simModel, applyScenarios) {
    for (let i = 0; i < env.quickControls.length; i++) {
      const ctrl = env.quickControls[i];
      let item = simModel.findItemById(ctrl.id);

      item.hasQuickControl = true;

      if (applyScenarios) {
        item.adjustedActive = ctrl.active;
        item.adjustedAmount = (ctrl.value / 100.0) * item.proposedAmount;
      }
    }
  }


  parseAndBind(env, model, simModel) {
    // JC 17-Mar-2024
    // As part of quick controls, moved the parameter setup to the simulator
    // and we can just reference here
    simModel.symbols.params = simModel.parameters;


    let num_milestones = simModel.milestones.length;
    for (let i = 0; i < num_milestones; i++) {
      const milestone = simModel.milestones[i];

      simModel.parseMilestoneDefinition(milestone);
    }

    let num_items = simModel.items.length;
    for (let i = 0; i < num_items; i++) {
      const item = simModel.items[i];

      if (item.baseName === "Expense127") {
        let xxx = 7;
      }
      simModel.parseItemDefinition(item);
    }


    simModel.evaluateAllExpressions();


    num_items = simModel.items.length;
    for (let i = 0; i < num_items; i++) {
      let item = simModel.items[i];

      item.startDate = simModel.getExpressionValue(item.startExprName);
      item.nextDate = simModel.getExpressionValue(item.startExprName);
      item.endDate = simModel.getExpressionValue(item.endExprName);
    }

  }


  /**
   * @param {SimulationModel} simModel
   * @param {Date} startDate 
   * @param {Date} endDate
   * @returns {TimelineResult} 
   */
  generateTimeline(simModel, startDate, endDate) {
    let years = [];
    let months = [];


    function findYear(year) {
      for (let y = 0; y < years.length; y++) {
        if (years[y].year === year) {
          return years[y];
        }
      }

      return null;
    }

    function findMonthOfDate(date) {
      const y = date.getFullYear();
      const m = date.getMonth();

      for (let i = 0; i < months.length; i++) {
        const mn = months[i];

        if (mn.year === y && mn.month === m + 1) {
          return mn;
        }
      }

      return null;
    }


    const today_date = new Date();
    const today_year = today_date.getFullYear();
    const today_month = today_date.getMonth();

    // let next_date = new Date( startDate );

    const end_date = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);


    const g = this.generator;
    const config = {
      startDate: startDate,
      endDate: end_date,
      freq: frequency.MONTHLY,

      amount: 0,
    }
    this.generator.init(config);

    let cur_month_idx = -1;
    let month_pos = 0;
    while (g.advance()) {
      const next_num = g.curDateNum;

      // Create the timeline object
      // next_date is always the first of the month but for reporting we want end of the end date
      const year = g.year;
      const month = g.month;
      const month_label = getZeroBasedMonthLabel(month);
      const display_label = month_label + ' ' + year.toString();

      let date = new Date(year, month + 1, 0);
      let unixDate = date.valueOf();

      let is_in_past = 1;
      if (year > today_year) {
        is_in_past = 0;
      } else if (year == today_year) {
        if (month >= today_month) {
          is_in_past = 0;
        }
      }

      if (year === today_year && month === today_month) {
        cur_month_idx = month_pos;
      }


      let cur_year = findYear(year);

      if (!is_in_past) {
        if (cur_year === null) {
          // FACTORY - Create YearContainer
          cur_year = {
            year: year,
            yearLabel: year.toString(),
            count: 0,

          }

          years.push(cur_year);
        }

        cur_year.count++;
      }


      // FACTORY - Create MonthContainer
      let month_obj = {
        index: month_pos,  // int

        date: date, // Javascript date end of month
        unixDate: unixDate, // milliseconds since epoch

        dateNum: next_num, // Simulator date number yyyymmdd first of month
        monthNum: Math.floor(next_num / 100), // yyyymm

        isInPast: is_in_past,  // int 0|1
        year: year,  // int
        month: month + 1, // one based month number, javascript is zero based
        monthLabel: month_label,  // string
        displayLabel: display_label,  // string

        yearOwner: cur_year, // YearContainer

        periodIndex: 0, // int

        monthTrans: [],
        monthSummaries: this.createAccountSubtotal(simModel.accts, simModel.summary),  // []
        monthTotals: this.createSubtotalObject(simModel.summary)  // []
      }

      months.push(month_obj);

      month_pos++;
    } // while 



    const birthday = simModel.findParameterByName("PrimaryBirthday").adjustedValue;

    const one_year = new Date();
    one_year.setFullYear(one_year.getFullYear() + 1);

    const retire_date = simModel.getMilestoneDate("PrimaryExpectedRetire");

    let cm = months[cur_month_idx];
    let m1 = findMonthOfDate(one_year);
    let m2 = findMonthOfDate(retire_date);
    let m3 = findMonthOfDate(endDate);

    // First, determine if the simulations starts on the same month as today, if not
    // we  have old updated accounts, so we want these months in their own period
    if (cur_month_idx > 0) {
      this.addReportingPeriod(simModel, months[0], months[cur_month_idx - 1], 'Past', true);
    }

    this.addReportingPeriod(simModel, cm, months[m1.index - 1], 'One Year');
    this.addReportingPeriod(simModel, m1, months[m2.index - 1], 'Working');
    this.addReportingPeriod(simModel, m2, m3, 'Retirement');

    let periods = simModel.periods;

    // Calculate period indexes and groupings
    let period_idx = 0; // We always have one period, which is the end of the entire model
    for (let m = 0; m < month_pos; m++) {
      const month = months[m];

      if (month.index > periods[period_idx].toMonth.index) {
        // We crossed into a new period
        if (period_idx < periods.length) {
          // Not the last periof
          period_idx++;
        }
      }

      month.periodIndex = period_idx;
      periods[period_idx].months.push(month);
    }

    for (let p=0; p<periods.length; p++) {
      const period = periods[p];
      period.displayLabel = `${period.fromMonth.displayLabel} ${period.toMonth.displayLabel}`;

    }

    // FACTORY - Create TimelineContainer
    let obj = {
      birthDate: birthday,
      todaysDate: today_date, // Date()
      startDate: startDate, // is always first of the month
      retireDate: retire_date,
      endDate: end_date, // is always last day of month

      minYear: years[0].year,
      maxYear: years[years.length - 1].year,

      curMonth: cm,
      retireMonth: m2,
      endMonth: m3,

      years: years, // [] YearContainer
      grandTotal: this.createSubtotalObject(simModel.summary),
      timeline: months // [] MonthContainer
    }

    return obj;
  }


  createAccountSubtotal(accts, summary) {
    const num_accts = accts.length;

    let subs = [];
    for (let a = 0; a < num_accts; a++) {
      var acct_obj = this.createSubtotalObject(summary);

      subs.push(acct_obj);
    }

    return subs;
  }


  createSubtotalObject(summary) {
    const num_categories = summary.length;
    let cat_groups = Categories.getCatGroupTypes();

    let obj = {
      calendarStartBal: 0,
      calendarEndBal: 0,
      monthDelinquent: 0,

      categories: [],    // total of each category of item for the given time period
      groupTotals: [],   // total of each category type for the given time period


      getTotalCredits: function (incTransfer) {
        let v = this.groupTotals[Categories.CATGROUP_INC] + this.groupTotals[Categories.CATGROUP_EIT];
        if (incTransfer) {
          v += this.groupTotals[Categories.CATGROUP_TFA];
        }

        return v;
      },

      getTotalDebits: function (incTransfer) {
        let v = this.groupTotals[Categories.CATGROUP_EXP] + this.groupTotals[Categories.CATGROUP_CIT];
        if (incTransfer) {
          v += this.groupTotals[Categories.CATGROUP_TTA];
        }

        return v;
      },

    }


    for (let s = 0; s < num_categories; s++) {
      obj.categories.push(0);
    }

    for (let g = 0; g < cat_groups.length; g++) {
      obj.groupTotals.push(0);
    }

    return obj;
  }

  /**
   * @param {SimulationEnvironment} env
   * @param {TimeResult} timeline
   * @param {SimulationModel} simModel
   * @returns {InitialTransactionResult}
   */
  generateInitialTransactions(env, timeline, simModel) {
    let initial_trans = [];

    this.generateInitialAccountTransactions(timeline, simModel, initial_trans);

    this.generateInitialItemTransactions(timeline, simModel, initial_trans);

    this.generateInitialLoanTransactions(timeline, simModel, initial_trans);

    this.generateInitialNetworthTransactions(timeline, simModel, initial_trans);


    var ret = {
      initialTrans: initial_trans
    }

    return ret;
  }


  /**
   * @param {TimelineResult} timeline
   * @param {SimulationAccount[]} accts
   * @param {InitialTransaction[]} initialTrans
   */
  generateInitialAccountTransactions(timeline, simModel, initialTrans) {
    const g = this.generator;

    let num_accts = simModel.accts.length;
    for (let i = 0; i < num_accts; i++) {
      const acct = simModel.accts[i];

      let start_date = timeline.startDate;
      if (acct.acctType === MM.ACCTTYPE_LINEOFCREDIT) {
        start_date = new Date(timeline.startDate.getFullYear(), timeline.startDate.getMonth(), acct.serviceDay);
      }

      const config = {
        startDate: start_date,
        endDate: timeline.endDate,
        freq: frequency.MONTHLY,

        amount: 0,
      }
      g.init(config);


      let trans_config = this.createInitialTransConfig(g, initialTrans, INIT_TRANSTYPE_ACCT, acct);
      if (acct.isAsset) {
        trans_config.toIdx = this.getCategoryIndex(simModel.summary, "EarnedInterest");
      } else {
        trans_config.fromIdx = this.getCategoryIndex(simModel.summary, "ChargedInterest");
      }


      // Next we generate a monthly transaction for each account to compute interest
      while (g.advance()) {

        trans_config.month = this.getMonthFromDatenum(timeline.timeline, g.curDateNum);

        let trans = this.createInitialTrans(trans_config);
      }
    } // for each account
  }


  /**
   * @param {TimelineResult} timeline
   * @param {SimulationAccount[]} accts
   * @param {InitialTransaction[]} initialTrans
   */
  generateInitialItemTransactions(timeline, simModel, initialTrans) {
    let model_start = timeline.startDate;

    const num_items = simModel.items.length;
    for (let i = 0; i < num_items; i++) {
      const item = simModel.items[i];

      if (item.endDate <= timeline.todaysDate) {
        item.nextDate = item.endDate;
      } else {
        item.nextDate = frequency.getNextDateAfterDate(item.startDate, item.freqNum, timeline.todaysDate);;
      }

      if (!item.adjustedActive)
        continue;

      // Start the analysis at start of month unless start date is in the future
      let next_date = new Date(item.startDate);
      if (item.freqNum === frequency.ONCE) {
        if (item.startDate < model_start)
          continue;
      } else {
        next_date = frequency.getNextDateAfterDate(item.startDate, item.freqNum, model_start);
      }


      const config = {
        startDate: next_date,
        endDate: item.endDate,
        freq: item.freqNum,

        amount: item.adjustedAmount,
      }
      this.generator.init(config);

      for (let r = 0; r < item.ruleIDs.length; r++) {
        this.generator.addRuleId(item.ruleIDs[r]);
      }


      this.generateTransactionRange(timeline, initialTrans, item);
    } // foreach item
  }


  generateTransactionRange(timeline, initialTrans, item) {

    const g = this.generator;

    let trans_config = this.createInitialTransConfig(g, initialTrans, INIT_TRANSTYPE_ITEM, item);
    trans_config.fromIdx = item.fromCatIdx;
    trans_config.toIdx = item.toCatIdx;

    while (g.advance()) {

      trans_config.month = this.getMonthFromDatenum(timeline.timeline, g.curDateNum);
      trans_config.amt = g.final;

      // NOTE: Performance - first call of a date function takes longer than the rest
      let trans = this.createInitialTrans(trans_config);
    } // while next_date in range
  }


  generateInitialLoanTransactions(timeline, simModel, initialTrans) {
    let num_loans = simModel.loans.length;
    for (let i = 0; i < num_loans; i++) {
      const loan = simModel.loans[i];

      // Start the analysis at start of month unless start date is in the future
      let start_date = simModel.getItemStartDate(loan.name);
      let end_date = simModel.getItemStopDate(loan.name);

      let next_date = frequency.getNextDateAfterDate(start_date, frequency.MONTHLY, simModel.startDate);

      const g = this.generator;
      const config = {
        startDate: next_date,
        endDate: end_date,
        freq: frequency.MONTHLY,

        amount: 0,
      }
      g.init(config);


      let trans_config = this.createInitialTransConfig(g, initialTrans, INIT_TRANSTYPE_LOAN, loan);

      // calculatePrincipalAndInterest
      while (g.advance()) {

        trans_config.month = this.getMonthFromDatenum(timeline.timeline, g.curDateNum);
        trans_config.amt = loan.totalPayment;

        let calc = this.calculatePrincipalAndInterest(simModel, loan, next_date); // l.currentValue

        let trans = this.createInitialTrans(trans_config);
      }

    } // for each loan  

  }


  /**
   * @param {TimelineResult} timeline
   * @param {InitialTransaction[]} initialTrans
   */
  generateInitialNetworthTransactions(timeline, simModel, initialTrans) {
    // Generate an instruction to compute the net worth at the end of each
    // year within the simulation date range
    const networth_element = simModel.elements[0]; // TODO - temp

    const g = this.generator;
    const config = {
      startDate: new Date(timeline.startDate.getFullYear(), 11, 31),
      endDate: new Date(timeline.endDate.getFullYear(), 11, 31),
      freq: frequency.YEARLY,

      amount: 0,
    }
    g.init(config);


    let trans_config = this.createInitialTransConfig(g, initialTrans, INIT_TRANSTYPE_NETWORTH, networth_element);

    while (g.advance()) {

      trans_config.month = this.getMonthFromDatenum(timeline.timeline, g.curDateNum);

      let trans = this.createInitialTrans(trans_config);
    }
  }


  createInitialTransConfig(gen, initialTrans, initType, source) {
    let config = {
      gen,
      initialTrans,
      initType,
      source,

      fromIdx: 0,
      toIdx: 0,

      month: null,
      amt: 0
    }

    return config;
  }


  createInitialTrans(config) {
    let trans = {
      initTranType: config.initType,
      source: config.source,

      fromIdx: config.fromIdx,
      toIdx: config.toIdx,

      monthObj: config.month,
      dateNum: config.gen.curDateNum,
      proposedAmount: config.amt,
      actualAmount: config.amt,
      delinquentAmount: 0
    }

    config.initialTrans.push(trans);
  }


  getMonthFromDatenum(timeline, dateNum) {
    const month_num = Math.floor(dateNum / 100);
    const num_months = timeline.length;

    let b = 0;
    let t = num_months - 1;
    let pos = -1;

    let safety = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      pos = Math.floor((b + t) / 2);
      let d = timeline[pos].monthNum - month_num;

      if (d === 0) {
        break;
      } else if (d < 0) {
        b = pos + 1
      } else {
        t = pos - 1
      }

      safety++;
      if (safety > 100)
        break;
    }


    return timeline[pos];
  }


  /**
   * 
   * @param {any} items
   * @param {InitialTransaction[]} initialTrans
   * @returns {FinalTransaction[]} 
   */
  processTransactions(simModel, initialTrans) {
    let num_transactions = initialTrans.length;
    let net_worth = [];

    const tt_cat = this.getCategoryIndex(simModel.summary, "TC");
    const tf_cat = this.getCategoryIndex(simModel.summary, "FB");
    const ins_cat = this.getCategoryIndex(simModel.summary, "Insurance");
    const inctax_cat = this.getCategoryIndex(simModel.summary, "Inctax");
    

    for (let i = 0; i < num_transactions; i++) {
      const tran = initialTrans[i];
      const source = tran.source;

      let amt = 0;

      if (tran.source.baseID === 49) { // Westjet
        if (tran.monthObj.year === 2024 && tran.monthObj.month === 10) {
          let t = tran;
        }
      }

      if (tran.initTranType === INIT_TRANSTYPE_ITEM) {
        amt = tran.proposedAmount;

        if (source.itemType === MM.ITEMTYPE_EXPENSE) {
          this.attemptTransaction(tran, source.fromSimAcct, null, tran.proposedAmount);
        } else if (source.itemType === MM.ITEMTYPE_INCOME) {
          this.attemptTransaction(tran, null, source.fromSimAcct, tran.proposedAmount);
        } else { // Transfer
          let fa = source.fromSimAcct;
          let ta = source.toSimAcct;

          if (source.subType !== SUBTYPE_FIXED) {
            // If its not fixed amount, then proposedAmount is a percent
            let run_bal = 0;

            // TODO: when using % of Dest, Dest has to be a Credit type account.
            if (source.subType === SUBTYPE_PCTSRC) { // Drain the source account
              run_bal = Math.abs(fa.runningBal);
            } else if (source.subType === SUBTYPE_PCTDST) { // Pay off destination account
              run_bal = Math.abs(ta.runningBal);
            }

            amt = tran.proposedAmount * run_bal / 100.0;
          }

          // Handle optional taxable amount on investments
          if (fa.acctType !== MM.ACCTTYPE_INVESTMENT) { // fa.acctType !== MM.ACCTTYPE_INVESTMENT  !fa.isTaxable
            this.attemptTransaction(tran, fa, ta, amt);
          } else {
            let taxable_amt = Math.min(amt, fa.runningBal - amt);
            if (taxable_amt > 0) {
              let after_tax_amt = fa.taxrate * taxable_amt / 100;
              after_tax_amt = 70 * taxable_amt / 100; // TODO - remove hard coded test

              this.attemptTransaction(tran, fa, ta, after_tax_amt);
              tran.fromIdx = inctax_cat;
              this.attemptTransaction(tran, fa, null, amt - after_tax_amt);
            }
          }
        } // transfer

      } else if (tran.initTranType === INIT_TRANSTYPE_ACCT) { // Account interest calculation
        amt = Math.abs(source.rate * source.runningBal);

        if (amt >= 0.01) {
          if (source.isAsset) { // Earned interest
            this.attemptTransaction(tran, null, source, amt);
          }
          // In the case of a line of credit, the interest is removed from the service
          // account and does not compound in the actual account
          else {
            this.attemptTransaction(tran, source, null, amt); // This is adding interest to the loc

            let act_amt = tran.actualAmount;
            if (source.hasInsurance === true) {
              tran.fromIdx = ins_cat;
              let ins = source.insurance;

              let raw_amt = Math.floor((-1 * source.runningBal / 1000)) * (ins.jointOwnerMod * ins.rate);
              let tax_amt = ins.premTax * raw_amt;
              let ins_amt = Math.round(((raw_amt + tax_amt) + Number.EPSILON) * 100) / 100;
              this.attemptTransaction(tran, source, null, ins_amt); // This is adding interest to the loc

              act_amt += tran.actualAmount;
            }
            if (source.acctType === MM.ACCTTYPE_LINEOFCREDIT) { // This transfers payment from service acct to loc
              tran.fromIdx = tt_cat;
              tran.toIdx = tf_cat;
              this.attemptTransaction(tran, source.serviceSimAcct, source, act_amt);
            }
          }
          if (tran.dateNum >= source.lastUpdateNum) {
            //console.log( source.baseDesc + amt.toFixed(2));
            source.totalInterest += amt;
          }
        }
      } else if (tran.initTranType === INIT_TRANSTYPE_LOAN) {
        amt = tran.proposedAmount;

        this.attemptTransaction(tran, source.fromSimAcct, null, amt);
      } else if (tran.initTranType === INIT_TRANSTYPE_NETWORTH) {
        let end_year_date = this.getDateFromDateNum(tran.dateNum);
        let net_worth_res = this.calculateNetWorthAtDate(simModel, end_year_date);

        let worth_obj = {
          date: end_year_date,
          unixDate: end_year_date.valueOf(),
          totalAssets: net_worth_res.totalAssets,
          totalLiabilities: net_worth_res.totalLiabilities,
        }

        net_worth.push(worth_obj);
      }

    } // for transaction

    let obj = {
      netWorth: net_worth
    }

    return obj;
  }

  /*
      var param_obj = {
        tran: null,
        debitReason: transtype.EXP_EXTERNAL,
        debitAcct: null,
        creditReason: transtype.INC_EXTERNAL,
        creditAcct: null,
        amount: amt,
      }
  */
  attemptTransaction(tran, debitAcct, creditAcct, proposedAmount) {
    const month = tran.monthObj;

    debugState(tran, 49, 2024, 10 , proposedAmount);

    let amt = proposedAmount;

    // Prevent paying more than is currently owed
    if (creditAcct !== null) {
      if (creditAcct.acctType === MM.ACCTTYPE_CREDITCARD || creditAcct.acctType === MM.ACCTTYPE_LINEOFCREDIT) {
        if (creditAcct.runningBal + amt > 0) {
          amt = Math.abs(creditAcct.runningBal);
        }
      }
    }


    let delinquent_amt = 0;
    if (debitAcct !== null) {

      if (debitAcct.runningBal - amt <= debitAcct.limit) {

        if (debitAcct.runningBal <= debitAcct.limit) {
          // Handle special boundary case of user starting over the limit
          delinquent_amt = amt;
          amt = 0;
        } else if (debitAcct.runningBal - proposedAmount <= debitAcct.limit) {
          // Normal case
          amt = debitAcct.runningBal - debitAcct.limit;
          delinquent_amt = proposedAmount - amt;
        }

        month.monthSummaries[debitAcct.index].monthDelinquent += delinquent_amt;
      }
    }

    // Prevent paying more than is currently owed, second check
    if (creditAcct !== null) {
      if (creditAcct.acctType === MM.ACCTTYPE_CREDITCARD || creditAcct.acctType === MM.ACCTTYPE_LINEOFCREDIT) {
        if (creditAcct.runningBal + amt > 0) {
          amt = Math.abs(creditAcct.runningBal);
        }
      }
    }


    tran.actualAmount = amt;
    tran.delinquentAmount = delinquent_amt;

    if (debitAcct !== null) {
      this.debitAccount(tran, tran.source.baseDesc, debitAcct, amt);
      month.monthSummaries[debitAcct.index].categories[tran.fromIdx] += amt;
    }

    if (creditAcct !== null) {
      this.creditAccount(tran, tran.source.baseDesc, creditAcct, amt);
      month.monthSummaries[creditAcct.index].categories[tran.toIdx] += amt;
    }

    //debugState(tran, 49, 2024, 10 , proposedAmount);


    function debugState(tran, itemId, year, month, proposed) {
      if (tran.source.baseID === itemId) { 
        if (tran.monthObj.year === year && tran.monthObj.month === month) {
          console.log( "proposed:    " + proposed.toFixed(2));
          console.log( "actual:      " + tran.actualAmount.toFixed(2));
          console.log( "delinquant:  " + tran.delinquentAmount.toFixed(2));
          if (debitAcct !== null) {
            console.log( "debit acct:  " + debitAcct.runningBal.toFixed(2));
          }
          if (creditAcct !== null) {
            console.log( "credit acct: " + creditAcct.runningBal.toFixed(2));
          }
        }  
      }
    }
  }


  debitAccount(tran, desc, acct, amt) {
    if (amt === 0 && tran.delinquentAmount === 0)
      return;

    const month = tran.monthObj;

    if (tran.dateNum >= acct.lastUpdateNum) {
      acct.runningBal -= amt;
      acct.delinquentAmount += tran.delinquentAmount;
      //if ( tran.delinquentAmount !== 0 ) {
      //  console.log(acct.delinquentAmount.toFixed(2) + ':' + tran.delinquentAmount.toFixed(2));
      //}
    } else {
      acct.skippedAmt -= amt;
    }

    this.addTransaction(month, tran.source, -1, tran, desc, tran.fromIdx, acct, amt);
  }


  creditAccount(tran, desc, acct, amt) {
    if (amt === 0 && tran.delinquentAmount === 0)
      return;

    const month = tran.monthObj;

    if (tran.dateNum >= acct.lastUpdateNum) {
      acct.runningBal += amt;
    } else {
      acct.skippedAmt += amt;
    }

    this.addTransaction(month, tran.source, 1, tran, desc, tran.toIdx, acct, amt);
  }


  addTransaction(month, source, op, tran, desc, idx, acct, amt) {
    let obj = {
      op: op,
      dateNum: tran.dateNum, // YYYYMMDD
      monthPos: tran.monthObj.index,
      month: Math.floor(tran.dateNum / 100), // YYYYMM
      source: source,
      catIdx: idx,
      tranDesc: desc,
      account: acct,
      amount: amt,

      delinquentAmount: tran.delinquentAmount
    }

    month.monthTrans.push(obj);
  }


  /**
   * Determins the total liabilites and assets at a point in time to determine overall Net Worth
   * @param {SimulationModel} simModel 
   * @param {Date} date 
   */
  calculateNetWorthAtDate(simModel, date) {
    let total_assets = 0;
    let total_liabilities = 0;
    let group_bals = [];

    let group_types = MM.getAccountGroupTypes();
    for (let i = 0; i < group_types.length; i++) {
      let group_obj = {
        groupID: i,
        desc: group_types[i].desc,
        total: 0,
        maxLastUpdated: 0,

        entities: []
      }

      group_bals.push(group_obj);
    }


    for (let i = 0; i < simModel.accts.length; i++) {
      const a = simModel.accts[i];

      // Calculate asset and liability values
      group_bals[a.acctGroup].total += a.runningBal;

      let acct_obj = {
        id: a.baseID,
        desc: a.baseDesc,
        currentValue: a.runningBal,
        lastUpdateDays: a.lastUpdateDays,
        lastUpdatedMsg: formatDisplayDate(a.lastUpdate)
      }

      group_bals[a.acctGroup].entities.push(acct_obj);
      if (a.lastUpdateDays > group_bals[a.acctGroup].maxLastUpdated) {
        group_bals[a.acctGroup].maxLastUpdated = a.lastUpdateDays;
      }
    }


    // Calculate loan values
    group_bals[MM.GROUPTYPE_LOAN].total = 0;
    for (let i = 0; i < simModel.loans.length; i++) {
      const l = simModel.loans[i];

      let calc = this.calculatePrincipalAndInterest(simModel, l, date); // l.currentValue

      group_bals[MM.GROUPTYPE_LOAN].total -= calc;

      let loan_obj = {
        id: l.baseID,
        desc: l.baseDesc,
        currentValue: calc,
        lastUpdatedMsg: formatDisplayDate(date)
      }

      group_bals[MM.GROUPTYPE_LOAN].entities.push(loan_obj);
    }


    // Calculate asset values
    group_bals[MM.GROUPTYPE_ASSET].total = 0;
    for (let i = 0; i < simModel.assets.length; i++) {
      const a = simModel.assets[i];

      group_bals[MM.GROUPTYPE_ASSET].total += a.currentValue;

      let asset_obj = {
        id: a.assetID,
        desc: a.assetDesc,
        currentValue: a.currentValue,
        lastUpdatedMsg: formatDisplayDate(date)
      }

      group_bals[MM.GROUPTYPE_ASSET].entities.push(asset_obj);
    }


    for (let i = 0; i < group_types.length; i++) {
      if (group_types[i].isAsset) {
        total_assets += group_bals[i].total;
      } else {
        total_liabilities += group_bals[i].total;
      }

    }


    let worth = {
      totalAssets: total_assets,
      totalLiabilities: total_liabilities,
      groupTotals: group_bals,
    }

    return worth;
  }


  /**
   * Computes the monthly summaries
   * @param {TimelineEntry[]} timeline
   * @param {SimulationAccount[]} accounts
   * @returns {TransactionStatements} 
   */
  generateTransactionSummary(timespan, accounts, categories) {
    const timeline = timespan.timeline
    var acct_bals = [];

    const num_accts = accounts.length;

    // Adjust the starting balance to handle the case of accounts updated before model start
    for (let i = 0; i < num_accts; i++) {
      const acct = accounts[i];

      // skippedAmt will be positive or negative depending on types of transactions skipped
      var starting_amt = acct.startingBal - acct.skippedAmt;

      acct_bals.push(starting_amt);
    }


    const num_months = timeline.length;
    for (let m = 0; m < num_months; m++) {
      const month = timeline[m];

      month.monthTotals.liquidAssets = 0;
      month.monthTotals.fixedAssets = 0;
      month.monthTotals.liabilities = 0;


      for (let a = 0; a < num_accts; a++) {
        const sim_acct = accounts[a];
        const acct_summary = month.monthSummaries[sim_acct.index];


        for (let c = 0; c < acct_summary.categories.length; c++) {
          const cat = categories[c].cat;
          const val = acct_summary.categories[c];

          month.monthTotals.categories[c] += val;
          acct_summary.groupTotals[cat.group] += val;
          month.monthTotals.groupTotals[cat.group] += val;
        }

        acct_summary.calendarStartBal = acct_bals[a];
        acct_bals[a] += acct_summary.getTotalCredits(true) - acct_summary.getTotalDebits(true);
        acct_summary.calendarEndBal = acct_bals[a];

        // Rolling the balances into the month, these represent total net worth at start and end of month
        month.monthTotals.calendarStartBal += acct_summary.calendarStartBal;
        month.monthTotals.calendarEndBal += acct_summary.calendarEndBal;
        month.monthTotals.monthDelinquent += acct_summary.monthDelinquent;

        // Net worth at end of each month, broken out, should add up to calendarEndBal
        if (sim_acct.isAsset) {
          month.monthTotals.liquidAssets += acct_summary.calendarEndBal;
        } else {
          month.monthTotals.liabilities += acct_summary.calendarEndBal;
        }
      } // accounts
    } // months
  }


  /**
   * String must match enum in Core.cs
   * @param {string} type 
   */
  getSubTypeAsConst(type) {
    var ret = -1;

    if (type === '')
      return SUBTYPE_NONE;

    for (let i = 0; i < subTypes.length; i++) {
      if (utils.stringCompare(type, subTypes[i].code)) {
        return subTypes[i].subType;
      }
    }

    if (ret === -1) {
      throw "Invalid Item Type";
    }

    return ret;
  }

  //---------------------------------------------------------------------------------------
  // Private date functions
  //---------------------------------------------------------------------------------------


  /**
   * Given a date, return an integer representation of the date as YYYYMMDD
   * @param {Date} date 
   * @returns {number}
   */
  getDateNumFromDate(date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return year * 10000 + month * 100 + day;
  }


  /**
   * Given a date number, return a Javascript Date()
   * @param {number} dateNum 
   * @returns {Date}
   */
  getDateFromDateNum(dateNum) {
    const year = Math.floor(dateNum / 10000);
    var rem = dateNum - year * 10000;

    const month = Math.floor(rem / 100);
    const day = rem - month * 100;

    // Datenum has month start at 1, not 0 like Javascript
    return new Date(year, month - 1, day);
  }


  /**
   * Given an Item that implements a date range and a date, calculate current Net Worth
   * B(n) = A(1+i)^n − (P/i)[(1+i)^n − 1]
   * @param {*} item 
   * @param {Date} toDate 
   */
  calculatePrincipalAndInterest(simModel, item, toDate) {
    var start_date = simModel.getItemStartDate(item.name);
    var end_date = simModel.getItemStopDate(item.name);

    if (item.originalAmount === 0) {
      return 0;
    }

    if (toDate >= end_date) {
      return 0; // All paid off
    }

    // TODO case where rate is zero

    var num_months = monthDiff(start_date, toDate);

    var i1 = Math.pow((1 + item.rate), -item.period);
    var i2 = Math.pow((1 + item.rate), num_months);

    var payment = (item.rate * item.originalAmount) / (1 - i1);

    // Future Value given a present value after N periods
    var p1 = item.originalAmount * i2;

    // Future value of annuity given a payment after N periods
    var p2 = (payment / item.rate) * (i2 - 1);

    return p1 - p2;  // Remaining principal
  }

}


export const model_simulator = new Simulator();
